import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { isServer } from 'src/utils/environment';

type AriaAnnouncerContextType = {
  announcePolite: (message: string) => void;
  announceAssertive: (message: string) => void;
};

const AriaAnnouncerContext = createContext<AriaAnnouncerContextType | undefined>(undefined);

interface AriaAnnouncerProviderProps {
  children: React.ReactNode;
}

const AnnouncerProvider = ({ children }: AriaAnnouncerProviderProps) => {
  const politeTimeout = useRef<number>();
  const [politeMessage, setPoliteMessage] = useState('');
  const [politeKey, setPoliteKey] = useState(0);

  const assertiveTimeout = useRef<number>();
  const [assertiveMessage, setAssertiveMessage] = useState('');
  const [assertiveKey, setAssertiveKey] = useState(0);

  const announcePolite = useCallback((message: string) => {
    if (isServer()) return;

    window.clearTimeout(politeTimeout.current);
    setPoliteMessage(message);
    setPoliteKey(prev => prev + 1);

    politeTimeout.current = window.setTimeout(() => {
      setPoliteMessage('');
      politeTimeout.current = undefined;
    }, 10_000);
  }, []);

  const announceAssertive = useCallback((message: string) => {
    if (isServer()) return;

    window.clearTimeout(assertiveTimeout.current);
    setAssertiveMessage(message);
    setAssertiveKey(prev => prev + 1);

    assertiveTimeout.current = window.setTimeout(() => {
      setAssertiveMessage('');
      assertiveTimeout.current = undefined;
    }, 10_000);
  }, []);

  const value = useMemo<AriaAnnouncerContextType>(
    () => ({
      announcePolite: (message: string) => announcePolite(message),
      announceAssertive: (message: string) => announceAssertive(message),
    }),
    [announcePolite, announceAssertive],
  );

  return (
    <AriaAnnouncerContext.Provider value={value}>
      {children}
      <div id="announcer-polite" aria-live="polite" aria-atomic="true" className="pds-sr-only">
        <span key={politeKey}>{politeMessage}</span>
      </div>

      <div
        id="announcer-assertive"
        aria-live="assertive"
        aria-atomic="true"
        className="pds-sr-only"
      >
        <span key={assertiveKey}>{assertiveMessage}</span>
      </div>
    </AriaAnnouncerContext.Provider>
  );
};

export const useAnnouncer = (): AriaAnnouncerContextType => {
  const ctx = useContext(AriaAnnouncerContext);

  if (!ctx) {
    throw new Error('useAriaAnnouncer must be used within an AriaAnnouncerProvider');
  }

  return ctx;
};

export default AnnouncerProvider;
